/**
 * Phase 2L-A145 — PWS Franchisee explanation + invite-to-join page.
 *
 * Public URL: /pws-franchisee-network
 *
 * Purpose: recruit Pristine Water Systems franchisees into the Aware
 * partner-testing program. Explains the workflow, what franchisees
 * get, the customer experience, compliance disclaimers, and captures
 * enquiries via a single form (POST /api/pws-franchisee-interest).
 *
 * Deliberate scope guards:
 *   • NO NATA / accreditation / government-approved wording anywhere.
 *   • NO full Water Testing Australia rebrand — "Australia-wide
 *     partner testing program" is used as an optional tagline only.
 *   • NOT wired into the primary header/footer nav — direct URL only
 *     until user approves broader exposure.
 *   • Compliance disclaimer block appears above the fold in the last
 *     panel + repeats in the footer disclaimer strip.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, Package, Handshake, CheckCircle2, FlaskConical, MapPin,
  ShieldCheck, ClipboardList, Truck, FileText, Users, HelpCircle,
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { pushFormEvent } from "@/lib/analytics";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const HOW_STEPS = [
  { icon: Handshake, title: "You share your booking link",
    desc: "AWARE gives every franchisee a partner-attributed booking link. Share it in your quotes, invoices, service reminders, or as a QR code at the customer's tap." },
  { icon: ClipboardList, title: "Customer books and pays online",
    desc: "The customer picks their test (tank, bore or basic safety), completes the property questionnaire and pays via Stripe. You never touch payments." },
  { icon: Package, title: "Kit is posted (or provided locally)",
    desc: "AWARE can post a prepaid sample kit straight to the customer, or you can hand one over in-person on a service visit — whichever suits the job." },
  { icon: Truck, title: "Customer sends the sample back",
    desc: "The kit ships back to the AWARE lab in a prepaid return satchel. Customer gets automated status updates the whole way." },
  { icon: FlaskConical, title: "AWARE tests and issues the report",
    desc: "Every test is performed by AWARE. The customer receives a professional water-quality report with plain-English recommendations." },
  { icon: Users, title: "You follow up (if the customer asks)",
    desc: "When the report suggests corrective work the customer can be pointed back to their local PWS franchisee for a quote — but never automatically." },
];

const WHAT_YOU_GET = [
  { icon: Handshake, title: "Partner booking link",     desc: "Attribution-tracked link so every test that flows through you is recorded against your territory." },
  { icon: ShieldCheck, title: "Attribution tracking",   desc: "The site remembers where each customer came from — so no matter which suburb or channel, credit lands with you." },
  { icon: FileText, title: "Professional test reports", desc: "AWARE issues a clean, branded water-quality report to the customer directly. You see a copy for follow-up." },
  { icon: Package, title: "Optional kit fulfilment",    desc: "We can post kits Australia-wide or drop-ship batches to you for use on service calls." },
  { icon: MapPin, title: "Local service opportunity visibility", desc: "When a report indicates corrective work, you're the natural local option. Recommendations flow, but nothing is booked without the customer's approval." },
  { icon: CheckCircle2, title: "No lab setup required", desc: "You don't need a lab, a courier account, or a chemistry qualification. AWARE handles the science; you handle the local relationship." },
];

const CUSTOMER_EXPERIENCE = [
  { icon: ClipboardList, title: "Simple online booking",             desc: "Choose the test, answer a short property questionnaire, pay online, done." },
  { icon: Package,       title: "Clear kit instructions",             desc: "Every kit ships with photo-illustrated sampling steps and a prepaid return satchel." },
  { icon: FileText,      title: "Professional plain-English report", desc: "Results the customer can actually read, with prioritised recommendations." },
  { icon: Users,         title: "Local support when needed",          desc: "When corrective work is called for, we point the customer back to their local PWS franchisee." },
];

const FAQS = [
  { q: "Do I need to run a lab?",
    a: "No. Every water test is performed by AWARE Water Testing. Your role is purely local — sharing the link, occasionally handing over a kit, and following up with a quote if the customer asks." },
  { q: "Who performs the water testing?",
    a: "AWARE performs every water test and issues the report. The local PWS franchisee is not the laboratory." },
  { q: "Can customers book online?",
    a: "Yes. Customers book and pay online through your partner-attributed booking link. Attribution is captured automatically." },
  { q: "Can I give customers a local link?",
    a: "Yes. Every franchisee has a partner-attribution link that credits testing back to your territory. We're rolling out per-partner landing pages as well — one URL per franchisee for QR codes and quote docs." },
  { q: "How are kits posted?",
    a: "AWARE ships prepaid Australia Post kits directly to the customer's address. Alternatively you can carry a small stock and hand one over on a service visit." },
  { q: "What happens if the report recommends service work?",
    a: "Recommendations are not quotes. Where corrective service is called for, the customer can be pointed back to their local PWS franchisee — but no work is booked without the customer explicitly asking. Any quote and any work is arranged directly between the customer and the local provider." },
  { q: "Is this available Australia-wide?",
    a: "Yes. The partner testing program is designed to operate Australia-wide through the Pristine Water Systems franchisee network." },
  { q: "Is there a cost to join?",
    a: "Not at this stage. Register your interest below and we'll walk you through the current onboarding process, including any staged rollout in your region." },
  { q: "How and when do partner returns get paid?",
    a: "Every eligible paid, non-refunded booking attributed to your partner link creates a ledger entry an AWARE admin reviews. Approved entries are released on a monthly cadence. Franchisee-specific terms are confirmed in writing during onboarding — not published publicly." },
];

const INTEREST_OPTIONS = [
  { key: "join",                    label: "I want to join" },
  { key: "more_info",               label: "I want more information" },
  { key: "test_kit_workflow_first", label: "I want to test the kit workflow first" },
];

export default function PwsFranchiseeNetwork() {
  useSEO({
    title:       "AWARE Partner Pathway for PWS Franchisees",
    description: "AWARE Water Testing gives PWS Partners a professional water testing, analysis and reporting pathway for customers across Australia.",
    url:         `${SEO_DEFAULTS.baseUrl}/pws-franchisee-network`,
    type:        "website",
  });

  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    region: "", interest_type: "join", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.business_name.trim() || form.business_name.trim().length < 2) e.business_name = "Enter your business name";
    if (!form.contact_name.trim()  || form.contact_name.trim().length < 2)  e.contact_name  = "Enter your contact name";
    // Very light email shape check — server-side is authoritative.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))               e.email         = "Enter a valid email";
    if (!form.phone.trim() || form.phone.trim().length < 6)                  e.phone         = "Enter a phone number";
    if (!form.region.trim() || form.region.trim().length < 2)                e.region        = "Enter your region / territory";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/pws-franchisee-interest", {
        business_name: form.business_name.trim(),
        contact_name:  form.contact_name.trim(),
        email:         form.email.trim(),
        phone:         form.phone.trim(),
        region:        form.region.trim(),
        interest_type: form.interest_type,
        message:       form.message.trim() || null,
      });
      setSubmitted(true);
      pushFormEvent("contact_form_submit", {
        form_name: "pws_franchisee_interest",
        form_type: "franchisee_interest",
        partner_or_customer_type: form.interest_type || null,
      });
      if (data?.deduped) {
        toast.info("We've already got your enquiry — the team will be in touch shortly.");
      } else {
        toast.success("Enquiry received. Thanks — the team will be in touch.");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="pws-franchisee-network-page" className="min-h-screen bg-white">
      <Header />
      <main>
        {/* ─── HERO ─── */}
        <section className="relative pt-28 lg:pt-36 pb-14 lg:pb-20 overflow-hidden">
          <div className="absolute top-32 right-10 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl -z-10" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-10 lg:gap-14 items-center">
              {/* Left: existing text block (unchanged copy) */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs font-semibold uppercase tracking-wider mb-6">
                  <Handshake className="w-3.5 h-3.5" /> Pristine Water Systems partner program
                </div>
                <h1 data-testid="pws-fn-hero-h1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight">
                  Offer professional water testing without running a lab.
                </h1>
                <p data-testid="pws-fn-hero-subtitle" className="mt-3 text-sm sm:text-base text-cyan-800 font-semibold uppercase tracking-wider">
                  Australia-wide partner testing program · powered by AWARE Water Testing
                </p>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                  A simple partner pathway for Pristine Water Systems franchisees to offer water testing kits, customer reports and local follow-up opportunities through AWARE Water Testing. You handle the local relationship — AWARE handles the lab.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    onClick={() => scrollTo("register-interest")}
                    data-testid="pws-fn-cta-register-interest"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white h-11 px-6 rounded-full font-semibold"
                  >
                    Register interest <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button
                    onClick={() => scrollTo("how-it-works")}
                    variant="outline"
                    data-testid="pws-fn-cta-how-it-works"
                    className="h-11 px-6 rounded-full font-semibold border-slate-300"
                  >
                    How it works
                  </Button>
                </div>
              </motion.div>

              {/* Right: hero illustration. Feb 2026 hand-back —
                  replaced with a single composed image supplied by
                  the user that already bakes in the eyebrow band
                  ("PWS + AWARE partner workflow") and the bottom
                  step-footer (PWS · Aus Post · AWARE). We render it
                  as one image inside the card wrapper — no external
                  eyebrow / step-footer needed. */}
              <motion.div
                data-testid="pws-fn-hero-illustration"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="relative"
              >
                <div className="relative w-full rounded-3xl overflow-hidden bg-white border border-cyan-100 shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_pristine-lab/artifacts/akylmo53_AWARE%20Partner%20Image.png"
                    alt="PWS + AWARE partner workflow: on the left, a Pristine Water Systems franchisee in a navy PWS-branded polo and cap steps forward from a Pristine Water Systems service van and launches a clear water-sample bottle in a splash of water; on the right, a smiling AWARE Water Testing lab technician in a white AWARE-branded lab coat with blue nitrile gloves receives an AWARE-branded shipping tube (marked FRAGILE / Water Samples) at a bright modern lab bench with a rack of blue-capped sample tubes and a microscope, with the AWARE Water Testing logo on the wall behind her. Below the scene, three logos label the workflow: 01 · Handover (Pristine Water Systems), 02 · Post to Lab (Australia Post Express), 03 · Analysis (AWARE Water Testing Lab Report)."
                    data-testid="pws-fn-hero-illustration-img"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="block w-full h-auto select-none"
                    draggable={false}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section id="how-it-works" className="py-16 lg:py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-700 mb-2">How it works</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
                A six-step partner workflow — no lab, no couriers, no chemistry.
              </h2>
            </div>
            <ol data-testid="pws-fn-how-it-works-list" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {HOW_STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={s.title} data-testid={`pws-fn-how-step-${i + 1}`} className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-9 h-9 rounded-full bg-cyan-100 text-cyan-800 font-bold flex items-center justify-center text-sm">{i + 1}</span>
                      <Icon className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div className="font-semibold text-slate-900 text-base leading-snug">{s.title}</div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        {/* ─── WHAT FRANCHISEES GET ─── */}
        <section className="py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-700 mb-2">What franchisees get</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
                Everything you need to offer testing — and nothing you don&apos;t.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {WHAT_YOU_GET.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} data-testid={`pws-fn-benefit-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-2xl border border-slate-200 p-6 bg-white hover:border-cyan-300 transition-colors">
                    <Icon className="w-6 h-6 text-cyan-600 mb-3" />
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── CUSTOMER EXPERIENCE ─── */}
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mb-12">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-700 mb-2">Customer experience</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
                A clean, professional experience — with you in the loop.
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CUSTOMER_EXPERIENCE.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="rounded-2xl border border-slate-200 p-5 bg-white">
                    <Icon className="w-5 h-5 text-cyan-600 mb-2" />
                    <div className="font-semibold text-slate-900 text-sm">{c.title}</div>
                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── HOW PARTNERS GET PAID ───
            Feb 2026 hand-back — implicit commission mention with
            monthly cadence. Deliberate constraints:
             • No specific rates, percentages or dollar amounts anywhere.
             • Preferred-partner framing (return / cadence) — not
               "commission" in the hero copy.
             • Rate-setting stays inside the private onboarding pack.
        */}
        <section id="preferred-partner-returns" className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-700 mb-2 flex items-center gap-2">
                <Handshake className="w-3.5 h-3.5" /> Preferred-partner returns
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
                How partners get paid.
              </h2>
            </div>
            <div
              data-testid="pws-fn-preferred-partner-returns-block"
              className="rounded-2xl border border-cyan-200 bg-cyan-50/60 p-6 sm:p-8"
            >
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                Every eligible paid, non-refunded booking attributed to your partner link
                creates a ledger entry that an AWARE admin reviews before release. Approved
                returns are settled on a <strong className="text-slate-900">monthly cadence</strong>{" "}
                once your onboarding is complete.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-700 leading-relaxed list-disc pl-5">
                <li data-testid="pws-fn-returns-eligible">
                  Only eligible paid bookings are counted. Unpaid, cancelled, refunded, void, failed and demo/test bookings never generate a payable entry.
                </li>
                <li data-testid="pws-fn-returns-review">
                  An AWARE admin reviews each ledger entry against attribution and payment status before it moves to <em>payable</em>.
                </li>
                <li data-testid="pws-fn-returns-cadence">
                  Approved returns are settled on a monthly cadence. Timing and settlement method are confirmed in your onboarding pack.
                </li>
                <li data-testid="pws-fn-returns-private-terms">
                  Franchisee-specific terms — including the exact return per test — are private to your onboarding pack and confirmed in writing before your first payout. They are not published publicly.
                </li>
              </ul>
              <div className="mt-5 text-[11px] text-slate-500 leading-relaxed" data-testid="pws-fn-returns-private-disclaimer">
                We don&apos;t publish partner return rates on this page — those are set per-franchisee and communicated privately during onboarding.
              </div>
            </div>
          </div>
        </section>

        {/* ─── COMPLIANCE / DISCLAIMER ─── */}
        <section className="py-14 lg:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              data-testid="pws-fn-compliance-block"
              className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-6 sm:p-8"
            >
              <div className="flex items-center gap-2 text-amber-900 font-bold text-sm uppercase tracking-wider mb-4">
                <ShieldCheck className="w-4 h-4" /> Important — compliance &amp; role clarity
              </div>
              <ul className="space-y-2.5 text-sm text-amber-950 leading-relaxed list-disc pl-5">
                <li data-testid="pws-fn-disclaimer-scwt-is-lab">Testing is performed by AWARE Water Testing.</li>
                <li data-testid="pws-fn-disclaimer-partner-not-lab">The local PWS partner is not the laboratory.</li>
                <li data-testid="pws-fn-disclaimer-no-work-without-approval">No service work is carried out without the customer&apos;s approval.</li>
                <li data-testid="pws-fn-disclaimer-recs-not-quotes">Recommendations are not quotes.</li>
                <li data-testid="pws-fn-disclaimer-work-arranged-separately">Any service work is arranged separately between the customer and the local provider.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ─── REGISTER INTEREST FORM ─── */}
        <section id="register-interest" className="py-16 lg:py-24 bg-slate-900 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-400 mb-2">Register interest</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">
                Interested? We&apos;ll walk you through it.
              </h2>
              <p className="mt-3 text-slate-300 text-base leading-relaxed">
                Rollout can be staged by region and franchisee. Let us know a bit about your territory and we&apos;ll be in touch to show you the kit, the customer journey and the partner dashboard.
              </p>
            </div>

            {submitted ? (
              <div
                data-testid="pws-fn-submit-success"
                className="rounded-2xl bg-emerald-500/10 border border-emerald-500/40 p-8 text-center"
              >
                <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
                <div className="font-display text-2xl font-bold">Thanks — we&apos;ve got it.</div>
                <p className="mt-3 text-emerald-100 max-w-md mx-auto leading-relaxed">
                  Someone from the partner-network team will reach out within a few business days. Keep an eye on <span className="font-semibold">{form.email}</span> for the next step.
                </p>
              </div>
            ) : (
              <form
                data-testid="pws-fn-interest-form"
                onSubmit={submit}
                noValidate
                className="rounded-2xl bg-white text-slate-900 p-6 sm:p-8 shadow-2xl"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pws-fn-business_name" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Franchisee / business name</Label>
                    <Input
                      id="pws-fn-business_name"
                      data-testid="pws-fn-input-business_name"
                      value={form.business_name}
                      onChange={(e) => setField("business_name", e.target.value)}
                      className="mt-1 h-11"
                      placeholder="e.g. Pristine Water Systems Noosa"
                      maxLength={200}
                    />
                    {errors.business_name && <div data-testid="pws-fn-error-business_name" className="mt-1 text-xs text-rose-600">{errors.business_name}</div>}
                  </div>
                  <div>
                    <Label htmlFor="pws-fn-contact_name" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Contact name</Label>
                    <Input
                      id="pws-fn-contact_name"
                      data-testid="pws-fn-input-contact_name"
                      value={form.contact_name}
                      onChange={(e) => setField("contact_name", e.target.value)}
                      className="mt-1 h-11"
                      placeholder="Your name"
                      maxLength={120}
                    />
                    {errors.contact_name && <div data-testid="pws-fn-error-contact_name" className="mt-1 text-xs text-rose-600">{errors.contact_name}</div>}
                  </div>
                  <div>
                    <Label htmlFor="pws-fn-email" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
                    <Input
                      id="pws-fn-email"
                      type="email"
                      data-testid="pws-fn-input-email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className="mt-1 h-11"
                      placeholder="you@yourbusiness.com.au"
                      maxLength={200}
                    />
                    {errors.email && <div data-testid="pws-fn-error-email" className="mt-1 text-xs text-rose-600">{errors.email}</div>}
                  </div>
                  <div>
                    <Label htmlFor="pws-fn-phone" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Phone</Label>
                    <Input
                      id="pws-fn-phone"
                      data-testid="pws-fn-input-phone"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      className="mt-1 h-11"
                      placeholder="0400 000 000"
                      maxLength={40}
                    />
                    {errors.phone && <div data-testid="pws-fn-error-phone" className="mt-1 text-xs text-rose-600">{errors.phone}</div>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pws-fn-region" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Region / territory</Label>
                    <Input
                      id="pws-fn-region"
                      data-testid="pws-fn-input-region"
                      value={form.region}
                      onChange={(e) => setField("region", e.target.value)}
                      className="mt-1 h-11"
                      placeholder="e.g. Sunshine Coast QLD, Adelaide SA, Hunter Valley NSW"
                      maxLength={200}
                    />
                    {errors.region && <div data-testid="pws-fn-error-region" className="mt-1 text-xs text-rose-600">{errors.region}</div>}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2 block">Interest type</Label>
                    <div data-testid="pws-fn-interest-type-group" className="grid sm:grid-cols-3 gap-2">
                      {INTEREST_OPTIONS.map((opt) => (
                        <button
                          type="button"
                          key={opt.key}
                          onClick={() => setField("interest_type", opt.key)}
                          data-testid={`pws-fn-interest-${opt.key}`}
                          className={`text-left rounded-xl border p-3 transition-all ${
                            form.interest_type === opt.key
                              ? "border-cyan-600 bg-cyan-50 ring-2 ring-cyan-600/20"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`mt-1 w-3 h-3 rounded-full border-2 flex-shrink-0 ${form.interest_type === opt.key ? "bg-cyan-600 border-cyan-600" : "border-slate-300"}`} />
                            <div className="text-sm font-semibold text-slate-900 leading-snug">{opt.label}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="pws-fn-message" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Short message <span className="normal-case text-slate-400 tracking-normal">(optional)</span></Label>
                    <Textarea
                      id="pws-fn-message"
                      data-testid="pws-fn-input-message"
                      value={form.message}
                      onChange={(e) => setField("message", e.target.value)}
                      className="mt-1"
                      rows={4}
                      maxLength={4000}
                      placeholder="Anything we should know about your customer base, service radius or preferred rollout timing?"
                    />
                  </div>
                </div>
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs text-slate-500">
                    We&apos;ll only use these details to talk about the partner program.
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="pws-fn-submit-btn"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white h-11 px-6 rounded-full font-semibold w-full sm:w-auto"
                  >
                    {submitting ? "Sending…" : "Register interest"}
                    {!submitting && <ArrowRight className="w-4 h-4 ml-1" />}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="py-16 lg:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <div className="text-xs uppercase tracking-wider font-bold text-cyan-700 mb-2 flex items-center gap-2">
                <HelpCircle className="w-3.5 h-3.5" /> Frequently asked
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Franchisee FAQ</h2>
            </div>
            <div data-testid="pws-fn-faq-list" className="space-y-3">
              {FAQS.map((f, i) => (
                <details key={f.q} data-testid={`pws-fn-faq-${i + 1}`} className="group rounded-2xl border border-slate-200 bg-white p-5 open:border-cyan-300 open:bg-cyan-50/40">
                  <summary className="cursor-pointer font-semibold text-slate-900 flex items-center justify-between list-none">
                    <span>{f.q}</span>
                    <ArrowRight className="w-4 h-4 text-cyan-600 transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FOOTER DISCLAIMER STRIP ─── */}
        <section className="py-8 border-t border-slate-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-500 leading-relaxed">
            Testing is performed by AWARE Water Testing. The local PWS partner is not the laboratory. Recommendations are not quotes. No service work is carried out without the customer&apos;s approval.
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
